import type { BankTemplateCategory, QuestionnaireFrequency, VerboseQuestionnaireMetadata } from '@gcv/shared';
import { BankTemplateType } from '@gcv/shared';
import { injectable } from 'inversify';
import { action, makeAutoObservable, runInAction } from 'mobx';

import { QuestionnairesApi } from 'api';
import type { Questionnaire } from 'services/questionnaire.service';
import { getFiBankStore } from './FiBankStore';

export enum QuestionnaireStatus {
  Draft = 'Draft',
  Modified = 'Unpublished Changes',
  Published = 'Published',
  Suppressed = 'Suppressed'
}

export interface QuestionnaireData extends Questionnaire {
  bankId: string;
  name: string;
  description: string;
  frequency: QuestionnaireFrequency;
  timeToRespond: string;
  status: QuestionnaireStatus;
  modified: string;
  created: string;
  distribution: string;
  templateCategory?: BankTemplateCategory;
  is_managed_template?: boolean;
  metadata?: any;
}
@injectable()
export class FiQuestionnaireStore {
  questionnaire: Questionnaire | undefined = undefined;
  questionnaireLimit = 0;
  questionnaires: { [templateId: string]: VerboseQuestionnaireMetadata } = {};
  allQuestionnaires: { [templateId: string]: VerboseQuestionnaireMetadata } = {};
  api: QuestionnairesApi = new QuestionnairesApi();
  loadingQuestionnaires = false;
  mappedQuestionnaires: QuestionnaireData[] = [];
  isLoaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  setQuestionnaire = action((questionnaire: Questionnaire | QuestionnaireData) => {
    this.questionnaire = questionnaire;
  });

  getQuestionnaires = async () => {
    const createStatus = (questionnaire: VerboseQuestionnaireMetadata): QuestionnaireStatus => {
      if (questionnaire.published_date) {
        return questionnaire.draft_exists ? QuestionnaireStatus.Modified : QuestionnaireStatus.Published;
      } else {
        return QuestionnaireStatus.Draft;
      }
    };
    runInAction(() => {
      this.loadingQuestionnaires = true;
    });

    const response = await this.api.getQuestionnairesForBank(getFiBankStore().bank.id, true);
    const [nonArchivedQuestionnairesMap, allQuestionnairesMap]: {
      [templateId: string]: VerboseQuestionnaireMetadata;
    }[] = [{}, {}];

    Object.entries(response.questionnaires).forEach(([id, q]) => {
      if (!q.archived) nonArchivedQuestionnairesMap[id] = q;
      allQuestionnairesMap[id] = q;
    });
    const questionnairesArray = Object.keys(nonArchivedQuestionnairesMap).map((id) => {
      return { ...response.questionnaires[id], id: id };
    });
    runInAction(() => {
      this.questionnaires = nonArchivedQuestionnairesMap;
      this.allQuestionnaires = allQuestionnairesMap;
      this.questionnaireLimit = response.questionnaires_allowed;

      this.mappedQuestionnaires = questionnairesArray.map((q) => {
        return {
          id: q.id,
          bankId: getFiBankStore().bank.id, // Banks don't share questionnaires, do they?
          name: q.name,
          description: q.description,
          frequency: typeof q.schedule.frequency === 'number' ? 'monthly' : q.schedule.frequency, // We have bad development data.
          timeToRespond: q.schedule.time_to_respond?.toString(),
          status: createStatus(q),
          modified: q.date_updated,
          created: q.date_created,
          distribution: q.distribute ? 'ON' : 'OFF',
          templateId: q.template_id,
          dateCreated: q.date_created,
          createdBy: q.created_by,
          customRequirements: q.custom_requirement_ids,
          templateType: BankTemplateType.Questionnaire,
          version: q.latest_version,
          distributedDate: q.distributed_date,
          draftExists: q.draft_exists,
          updatedBy: q.updated_by,
          templateCategory: q.template_category,
          is_managed_template: q.is_managed_template ?? false,
          metadata: (q as any).metadata
        } as QuestionnaireData;
      });

      this.isLoaded = true;
      this.loadingQuestionnaires = false;
    });
  };

  getManagedQuestionnaires = async () => {
    if (!this.isLoaded) {
      await this.getQuestionnaires();
    }
    return Object.values(this.questionnaires).filter((q) => q.is_managed_template);
  };
}

let questionnaireStore: FiQuestionnaireStore | undefined;

export function getFiQuestionnaireStore(): FiQuestionnaireStore {
  if (!questionnaireStore) {
    questionnaireStore = new FiQuestionnaireStore();
  }

  return questionnaireStore;
}
